import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** Delay before a hover on the cart icon opens the drawer, in ms. */
  #hoverOpenDelay = 200;

  /** Grace period after the pointer leaves both the icon and the drawer, in ms. */
  #hoverCloseDelay = 350;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #hoverOpenTimer = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #hoverCloseTimer = null;

  /**
   * Set once the drawer stops being a passive hover preview — the shopper clicked or focused
   * something inside it, or it was auto-opened by an add to cart. From then on it only closes
   * the normal ways (X, Esc, backdrop) so we never yank it away mid-interaction.
   */
  #hoverAutoCloseDisabled = false;

  /**
   * True while the drawer is opening — `showModal()` moving focus inside, and the 125ms
   * slide-in animation. During that window focus is not shopper intent and the drawer's
   * measured rect is still mid-animation, so both signals must be ignored.
   */
  #hoverOpenSettling = false;

  /** Last seen mouse position, viewport coordinates. @type {{x: number, y: number} | null} */
  #lastPointer = null;

  /** Tears down the document-level pointer tracking. @type {AbortController | null} */
  #hoverTrackingController = null;

  /** @type {MediaQueryList | null} */
  #hoverCapableQuery = null;

  /**
   * Header markup renders `header-actions` twice (desktop + mobile columns). Both include a
   * cart drawer; only one column is visible, but both listen for `cart:update` and would open.
   * @returns {HTMLElement | null}
   */
  static #primaryCartDrawerHost() {
    const hosts = [...document.querySelectorAll('cart-drawer-component')];
    if (hosts.length === 0) return null;
    if (hosts.length === 1) return hosts[0];

    const visible = hosts.filter((el) => {
      try {
        if (typeof el.checkVisibility === 'function') {
          return el.checkVisibility({
            checkOpacityZero: true,
            contentVisibilityAuto: true,
          });
        }
      } catch {
        /* ignore */
      }
      return el.offsetParent !== null;
    });

    return (visible.length ? visible : hosts)[0] ?? null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    if (this.hasAttribute('open-on-hover')) this.#bindHoverOpen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#unbindHoverOpen();
  }

  /* ------------------------------------------------------------------ */
  /* Hover preview                                                       */
  /* ------------------------------------------------------------------ */

  /** @returns {HTMLElement | null} */
  get #hoverTrigger() {
    return this.querySelector('[data-testid="cart-drawer-trigger"]');
  }

  #bindHoverOpen() {
    this.#hoverCapableQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    const trigger = this.#hoverTrigger;
    const dialog = this.refs.dialog;
    if (!trigger || !dialog) return;

    trigger.addEventListener('pointerenter', this.#handleTriggerPointerEnter);
    trigger.addEventListener('pointerleave', this.#handleTriggerPointerLeave);
    // Clicking through to the cart page should not leave a pending open behind it.
    trigger.addEventListener('click', this.#clearHoverTimers);

    // NB: no pointerleave on the dialog. A modal <dialog>'s ::backdrop hit-tests as the dialog
    // element itself, so the pointer never "leaves" it and `dialog:hover` stays true across the
    // whole viewport. Closing is driven by #handleHoverPointerMove comparing geometry instead.
    dialog.addEventListener('pointerdown', this.#commitToOpenDrawer);
    dialog.addEventListener('focusin', this.#commitToOpenDrawer);
    dialog.addEventListener('close', this.#stopHoverTracking);
    dialog.addEventListener('click', this.#handleCartIconClickThrough);
  }

  #unbindHoverOpen() {
    this.#clearHoverTimers();

    const trigger = this.#hoverTrigger;
    if (trigger) {
      trigger.removeEventListener('pointerenter', this.#handleTriggerPointerEnter);
      trigger.removeEventListener('pointerleave', this.#handleTriggerPointerLeave);
      trigger.removeEventListener('click', this.#clearHoverTimers);
    }

    const dialog = this.refs.dialog;
    if (dialog) {
      dialog.removeEventListener('pointerdown', this.#commitToOpenDrawer);
      dialog.removeEventListener('focusin', this.#commitToOpenDrawer);
      dialog.removeEventListener('close', this.#stopHoverTracking);
      dialog.removeEventListener('click', this.#handleCartIconClickThrough);
    }
    this.#stopHoverTracking();
  }

  /**
   * Hover is a desktop-only affordance. Touch fires a synthetic `pointerenter` right before
   * the tap, which would open the drawer and swallow the navigation to the cart page.
   * @param {PointerEvent} event
   */
  #isHoverPointer(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return false;
    return this.#hoverCapableQuery?.matches !== false;
  }

  /** The bubble class is kept in sync by `cart-icon.js`, so this stays accurate after AJAX updates. */
  #cartHasItems() {
    return Boolean(this.querySelector('.header-actions__cart-icon--has-cart'));
  }

  /**
   * Is the pointer over the drawer panel (or the cart icon that opens it)?
   *
   * This has to be geometric. `:hover` and `pointerleave` both report the pointer as being
   * inside a modal <dialog> anywhere on screen, because the ::backdrop hit-tests as the dialog.
   * @param {{x: number, y: number}} point
   */
  #pointIsOverDrawer({ x, y }) {
    const pad = 4;
    const rects = [this.refs.dialog?.getBoundingClientRect(), this.#hoverTrigger?.getBoundingClientRect()];

    return rects.some(
      (rect) =>
        rect && rect.width > 0 && x >= rect.left - pad && x <= rect.right + pad && y >= rect.top - pad && y <= rect.bottom + pad
    );
  }

  #clearHoverTimers = () => {
    if (this.#hoverOpenTimer) clearTimeout(this.#hoverOpenTimer);
    if (this.#hoverCloseTimer) clearTimeout(this.#hoverCloseTimer);
    this.#hoverOpenTimer = null;
    this.#hoverCloseTimer = null;
  };

  /** @param {Event} [event] */
  #commitToOpenDrawer = (event) => {
    // `showModal()` focuses the first focusable element in the drawer as it opens. Ignore that
    // one — otherwise every hover-opened drawer would immediately stop auto-closing.
    if (event?.type === 'focusin' && this.#hoverOpenSettling) return;

    this.#hoverAutoCloseDisabled = true;
    if (this.#hoverCloseTimer) clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = null;
    // The drawer is no longer a preview, so stop paying for a document-wide pointermove listener.
    this.#stopHoverTracking();
  };

  /** @param {PointerEvent} event */
  #handleTriggerPointerEnter = (event) => {
    if (!this.#isHoverPointer(event)) return;

    if (this.#hoverCloseTimer) clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = null;

    if (this.refs.dialog?.open) return;
    if (!this.#cartHasItems()) return;

    if (this.#hoverOpenTimer) clearTimeout(this.#hoverOpenTimer);
    this.#hoverOpenTimer = setTimeout(() => {
      this.#hoverOpenTimer = null;
      // Re-check: the cart can empty out, and only the visible header column should open.
      if (!this.#cartHasItems()) return;
      if (CartDrawerComponent.#primaryCartDrawerHost() !== this) return;

      this.#hoverAutoCloseDisabled = false;
      this.#hoverOpenSettling = true;
      this.open();
      this.#startHoverTracking();
      this.#settleHoverOpen();
    }, this.#hoverOpenDelay);
  };

  /**
   * Hold the settling flag until the drawer has finished sliding in, then re-test where the
   * pointer ended up — the shopper may have moved away during the animation, and a stationary
   * mouse emits no further `pointermove` to trigger the check.
   */
  #settleHoverOpen() {
    const dialog = this.refs.dialog;

    const done = () => {
      if (!this.#hoverOpenSettling) return;
      this.#hoverOpenSettling = false;
      dialog?.removeEventListener('animationend', done);
      this.#evaluateHoverPosition();
    };

    dialog?.addEventListener('animationend', done);
    // Fallback: the slide-in is 125ms (`--animation-speed`), but never hang on a missing event.
    setTimeout(done, 300);
  }

  #startHoverTracking() {
    this.#stopHoverTracking();
    this.#hoverTrackingController = new AbortController();
    document.addEventListener('pointermove', this.#handleHoverPointerMove, {
      capture: true,
      signal: this.#hoverTrackingController.signal,
    });
  }

  #stopHoverTracking = () => {
    this.#hoverTrackingController?.abort();
    this.#hoverTrackingController = null;
    this.#lastPointer = null;
  };

  /**
   * While the drawer is a hover preview, every mouse move re-tests the pointer against the
   * drawer's geometry. Moves over the backdrop still reach us — they target the dialog.
   * @param {PointerEvent} event
   */
  #handleHoverPointerMove = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;

    this.#lastPointer = { x: event.clientX, y: event.clientY };
    if (this.#hoverOpenSettling) return;

    this.#evaluateHoverPosition();
  };

  /** Arms or cancels the auto-close based on the last known pointer position. */
  #evaluateHoverPosition() {
    if (this.#hoverAutoCloseDisabled || !this.#lastPointer) return;
    if (!this.refs.dialog?.open) return;

    if (this.#pointIsOverDrawer(this.#lastPointer)) {
      if (this.#hoverCloseTimer) clearTimeout(this.#hoverCloseTimer);
      this.#hoverCloseTimer = null;
      return;
    }

    // Already counting down — let the running timer finish rather than restarting it on
    // every mouse move, which would keep the drawer open indefinitely.
    if (this.#hoverCloseTimer) return;

    this.#hoverCloseTimer = setTimeout(() => {
      this.#hoverCloseTimer = null;
      if (this.#hoverAutoCloseDisabled) return;
      if (!this.refs.dialog?.open) return;
      if (this.#lastPointer && this.#pointIsOverDrawer(this.#lastPointer)) return;
      this.#stopHoverTracking();
      this.close();
    }, this.#hoverCloseDelay);
  }

  /**
   * The drawer slides in over the header and covers the cart icon, so a click there lands on
   * whatever drawer content happens to sit underneath and goes nowhere. Worse, whether that
   * happens depends on how long the shopper hovered first — under the open delay the link is
   * still exposed and navigates. Re-expose the icon's one job: go to the cart page.
   * @param {MouseEvent} event
   */
  #handleCartIconClickThrough = (event) => {
    const trigger = this.#hoverTrigger;
    const href = trigger?.getAttribute('href');
    if (!href || !(event.target instanceof Element)) return;

    // Never steal a click meant for something the shopper can actually see and use.
    if (event.target.closest('a, button, input, select, textarea, [role="button"], [contenteditable]')) return;

    const rect = trigger.getBoundingClientRect();
    if (rect.width === 0) return;
    if (event.clientX < rect.left || event.clientX > rect.right) return;
    if (event.clientY < rect.top || event.clientY > rect.bottom) return;

    event.preventDefault();
    window.location.assign(href);
  };

  /**
   * Leaving the cart icon before the open delay elapses cancels the pending open. Once the
   * drawer is up, closing is handled by the pointer tracking above.
   * @param {PointerEvent} event
   */
  #handleTriggerPointerLeave = (event) => {
    if (!this.#isHoverPointer(event)) return;
    if (this.#hoverOpenTimer) clearTimeout(this.#hoverOpenTimer);
    this.#hoverOpenTimer = null;
  };

  /** @param {CartAddEvent | CartUpdateEvent} event */
  #handleCartAdd = async (event) => {
    if (event.detail?.data?.source !== 'product-form-component') return;
    if (!this.hasAttribute('auto-open')) return;

    const primary = CartDrawerComponent.#primaryCartDrawerHost();
    if (!primary || primary !== this) return;

    const closers = [];
    for (const host of document.querySelectorAll('cart-drawer-component')) {
      if (host === this) continue;
      const otherDialog = host.querySelector(':scope > dialog');
      if (otherDialog instanceof HTMLDialogElement && otherDialog.open) {
        closers.push(/** @type {CartDrawerComponent} */ (host).closeDialog());
      }
    }
    await Promise.all(closers);
    // An add-to-cart confirmation is not a hover preview — never let it auto-close.
    this.#commitToOpenDrawer();
    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
    for (const host of document.querySelectorAll('cart-drawer-component')) {
      if (host === this) continue;
      const d = host.querySelector(':scope > dialog');
      if (d instanceof HTMLDialogElement && d.open) {
        /** @type {CartDrawerComponent} */ (host).closeDialog();
      }
    }
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
